import { AddIcon } from "../AdminIcon";
// Components/Tabs/SelectLabTab.jsx

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

// Importing components
import Slider from "../Slider/Slider";
import SliderMenu from "../SliderMenu/SliderMenu";
import { MiniButton } from "../MiniButton/MiniButton";
import Backend from "../../../utils/api.js";
import LabNavigationTab from "./LabNavigationTab.jsx";
import NewLabTab from "./NewLabTab.jsx";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * SelectLabTab Component
 * 
 * This component allows administrators to select a lab from a list or create a new one.
 * It manages the display of labs based on user permissions and handles navigation to
 * specific lab details or the creation of new labs.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.setCurrentBadgrState - Function to set the current Badgr integration state.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 * @param {number} props.index - The current index in the tab stack for proper stacking order.
 */
function SelectLabTab({ user, pushTab, setCurrentBadgrState, revert, index }) {
  const history = useHistory();
  const [labListKey, setLabListKey] = useState(0);

  useDocumentTitle("Admin: Select Space Tab – Pinpoint");
  /**
   * setBadgrState
   * 
   * Fetches the Badgr integration state for a specific lab.
   * 
   * @param {string} labId - The unique identifier of the lab.
   * @param {number} UID - The unique identifier of the user.
   * @returns {string} - Returns 'True' if Badgr integration is enabled, otherwise 'False'.
   */
  const setBadgrState = async (labId, UID) => {
    try {
      const response = await Backend.getLabById({ id: labId, UID });
      const badgrIntegration = response.badgrIntegration;
      const badgrState = badgrIntegration ? 'True' : 'False';
      return badgrState;
    } catch (error) {
      console.log(error);
      return 'False'; // Default to 'False' in case of error
    }
  };

  /**
   * handleLabClick
   * 
   * Handles the event when a lab is clicked from the list. Fetches the Badgr state
   * and navigates to the LabNavigationTab if successful.
   * 
   * @param {Object} lab - The lab object that was clicked.
   */
  const handleLabClick = async (lab) => {
    try {
      const badgrState = await setBadgrState(lab.id, user.UID);
      setCurrentBadgrState(badgrState);
      
      // Push the LabNavigationTab onto the stack with updated index
      pushTab(
        <LabNavigationTab adminRoute="space"
          user={user}
          labId={lab.id}
          badgrState={badgrState}
          pushTab={pushTab}
          revert={revert}
          index={index + 1} // Increment index for stacking
          onLabChanged={() => setLabListKey(k => k + 1)}
        />
      );
    } catch (error) {
      console.error('Error in handleLabClick:', error);
      // Optionally, handle the error by showing a notification or alert
    }
  };

  /**
   * handleNewLab
   * 
   * Handles the event when the "Add New Lab" button is clicked. Pushes the NewLabTab
   * onto the stack for creating a new lab.
   */
  const handleNewLab = () => {
    pushTab(
      <NewLabTab adminRoute="newSpace"
        key={`newLab-${0}`}
        user={user}
        pushTab={pushTab}
        revert={revert}
        index={0}
        onLabChanged={() => setLabListKey(k => k + 1)}
      />
    );
  };

  /**
   * dataFunction
   * 
   * Fetches labs based on user permissions. If the user is a super admin, all labs
   * are returned. Otherwise, only labs the user has permissions for are returned.
   * 
   * @returns {Promise<Object>} - Resolves to an object containing labs and status.
   */
  const dataFunction = () => {
    return Promise.all([
      Backend.getLabPermissionsByUser(user.UID),
      Backend.getLabs(1000)
    ])
      .then(function (responses) {
        const labPermissionRes = responses[0];
        const labs = responses[1];

        if (labPermissionRes.error || labs.error) {
          return { items: [], status: 0 };
        }

        if (user.isSuperAdmin === true) {
          return {
            items: labs.items,
            status: 200
          };
        } else {
          const labPermissionIds = labPermissionRes.items
            .filter((l) => l.is_admin)
            .reduce((map, l) => {
              map[String(l.lab).toLowerCase()] = l;
              return map;
            }, {});
          const filteredLabs = labs.items.filter((l) => String(l.id).toLowerCase() in labPermissionIds);
          return {
            items: filteredLabs,
            status: 200
          };
        }
      })
      .catch((error) => {
        console.error('Error in dataFunction:', error);
        return {
          items: [],
          status: 500
        };
      });
  };
  
  /**
   * handleNavClick
   * 
   * Navigates the user to the "/profile" page when the navigation bar is clicked.
   */
  const handleNavClick = () => {
    history.push("/profile");
  };

  return (
    <Slider
      slide={true}
      index={index + 1} // Increment index for stacking
      navColor="rgb(210, 125, 40)"
      name="Space selection"
      contentTitle="Select your space"
      onClick={handleNavClick}    // Navigate to /profile on nav click
      toggle={labListKey}
      canGoBack={false}
    >
      {/* SliderMenu displays the list of labs */}
      <SliderMenu
        placeholderText="Find a space..."
        dataFunction={dataFunction}
        onClick={handleLabClick}
      />
      
      {/* MiniButton to add a new lab */}
      <MiniButton onClick={handleNewLab} type="add">
        <AddIcon boxSize={6} />
      </MiniButton>
    </Slider>
  );
}

// PropTypes for type-checking component props
SelectLabTab.propTypes = {
  user: PropTypes.object.isRequired,
  pushTab: PropTypes.func.isRequired,
  setCurrentBadgrState: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired, // Ensure 'index' is required
};

export default SelectLabTab;
